<script lang="ts">
  import Dropdown from "$app/components/Dropdown.svelte"
  import { xhr, toast } from "$app/utils"
  import Svelecte from "svelecte"
  import { CHANNEL_ICONS } from "$app/utils/channels"

  interface Props {
    channel_id: number
    status: string
    onupdate: (channel: ForumChannelType) => void
    oncreate: (channel: ForumChannelType) => void
  }

  let { channel_id, status = "", onupdate, oncreate }: Props = $props()

  let channel = {} as ForumChannelType

  const fields = ["name", "icon", "channel_type", "enabled"]

  let form = {} as ForumChannelType
  let selected_readers = $state(channel.reader_users) as UserType[]
  let selected_authors = $state(channel.author_users) as UserType[]
  let selected_admins = $state(channel.admin_users) as UserType[]

  let errors: any = $state({})
  let saving = $state(false)
  let openDropdown = $state(false)

  const iconOptions = Object.entries(CHANNEL_ICONS).map((icon) => {
    return icon[0]
  })

  async function saveChannel() {
    if (status === "edit") {
      await updateChannel()
    } else {
      await createChannel()
    }
  }

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      formData.append(`forum_channel[${field}]`, form[field] || "")
    })

    let base_param = `forum_channel[forum_channel_users_attributes][]`

    selected_admins?.forEach((field) => {
      formData.append(`${base_param}[user_id]`, field.id)
      formData.append(`${base_param}[role]`, "admin")
    })
    selected_authors?.forEach((field) => {
      formData.append(`${base_param}[user_id]`, field.id)
      formData.append(`${base_param}[role]`, "author")
    })
    selected_readers?.forEach((field) => {
      formData.append(`${base_param}[user_id]`, field.id)
      formData.append(`${base_param}[role]`, "reader")
    })
    return formData
  }

  function validForm(): boolean {
    errors = {}

    return Object.keys(errors).length === 0
  }

  let loading = $state(true)
  async function loadChannel(): Promise<void> {
    try {
      loading = true
      const resp = await xhr.get(
        `/admin/community_channels/${channel_id}/edit.json`
      )

      channel = resp.data
      selected_readers = channel.reader_users as UserType[]
      selected_authors = channel.author_users as UserType[]
      selected_admins = channel.admin_users as UserType[]
      setForm()
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      loading = false
    }
  }

  async function updateChannel(): Promise<void> {
    if (!validForm()) return

    try {
      saving = true
      const resp = await xhr.patch(
        `/admin/community_channels/${channel.id}.json`,
        getFormData()
      )

      toast("Channel has been updated", "success")
      onupdate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      saving = false
    }
  }

  async function createChannel(): Promise<void> {
    if (!validForm()) return
    try {
      saving = true
      const resp = await xhr.post(
        `/admin/community_channels.json`,
        getFormData()
      )

      toast("Channel has been created", "success")
      oncreate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      saving = false
    }
  }

  function setForm(): void {
    fields.forEach((field) => {
      if (["users"].includes(field)) {
        form[field] = channel[field] || []
      } else if (["enabled"].includes(field)) {
        form[field] = channel[field] ? "true" : "false"
      } else {
        form[field] = channel[field]
      }
    })
  }

  if (status === "edit") {
    loadChannel()
  } else if (status === "new") {
    loading = false
    setForm()
  }

  function userRenderer(item, isSelected) {
    if (isSelected) {
      return `<div class="bg-gray-100 text-gray-800 border border-gray-200 rounded text-sm user-item flex items-center">
        <img src=${item.avatar_thumb_url} class="w-5 h-5 mr-1" />
        ${item.name} (${item.email})
      </div>`
    }
    return `<div class="bg-gray-100 text-gray-800 border border-gray-200 rounded text-sm user-item flex items-center">
        <img src=${item.avatar_thumb_url} class="w-5 h-5 mr-1" />
        ${item.name} (${item.email})
      </div>`
  }

  let payload = null

  function onSubmit(e) {
    const object = {}
    const formData = new FormData(e.target)
    formData.forEach((value, key) => {
      if (object[key]) {
        object[key] += ", " + value
        return
      }
      object[key] = value
    })
    payload = JSON.stringify(object, null, 2)
  }

  function resetPayload(e) {
    payload = null
  }
</script>

<form>
  <h2 class="text-base font-semibold leading-6 text-gray-900">
    {status === "edit" ? "Edit" : "New"} Channel
  </h2>

  {#if loading}
    Loading
  {:else}
    <div class="flex">
      <div class="w-full px-2">
        <div class="input-cont">
          <label for="">Channel Name</label>
          <input
            class="input-admin w-full"
            type="text"
            bind:value={form.name}
          />
          <div class="text-red-500">
            {errors.name || ""}
          </div>
        </div>

        <div class="input-cont">
          <label for="">Channel Icon</label>
          <div class="relative">
            <Dropdown bind:open={openDropdown}>
              {#snippet button()}
                <span
                  class="bg-gray-900 text-gray-100 px-4 flex items-center py-2 w-full border rounded-lg border-gray-200 w-full"
                >
                  {#if form.icon}
                    {@const Icon = CHANNEL_ICONS[form.icon]}
                    <Icon class="h-8 w-8 text-gray-100" />
                    <span class="px-2"> Change Icon </span>
                  {:else}
                    <span class="px-2"> Select Icon </span>
                  {/if}
                </span>
              {/snippet}

              <div>
                <div
                  class="origin-top-right absolute left-14 top-0 mt-2 w-64 h-32 overflow-y-auto rounded-md shadow-lg bg-gray-800 ring-1 ring-gray-100 border border-gray-200 ring-opacity-5 focus:outline-none z-10"
                  role="menu"
                >
                  <div class="py-1 flex flex-wrap p-4" role="none">
                    {#each iconOptions as icon}
                      <button
                        class="w-12 h-12 flex items-center justify-center"
                        role="menuitem"
                        onclick={() => {
                          openDropdown = false
                          form.icon = icon
                          //updatePost({ is_pinned: !post.is_pinned })
                        }}
                      >
                        {#if CHANNEL_ICONS[icon]}
                          {@const Icon = CHANNEL_ICONS[icon]}
                          <Icon class="size-8 text-gray-100" />
                        {/if}
                      </button>
                    {/each}
                  </div>
                </div>
              </div></Dropdown
            >
          </div>
          <div class="text-red-500">
            {errors.icon || ""}
          </div>
        </div>

        <div class="input-cont">
          <label for="">Channel Type</label>
          <select class="input-admin" bind:value={form.channel_type}>
            <option value="public_channel">Public</option>
            <option value="private_channel">Private</option>
          </select>
          <div class="text-red-500">
            {errors.channel_type || ""}
          </div>
        </div>
        <div class="input-cont">
          <label for="">Enabled</label>
          <select class="input-admin" bind:value={form.enabled}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
          <div class="text-red-500">
            {errors.enabled || ""}
          </div>
        </div>
      </div>
    </div>

    {#if status === "edit" && form.channel_type === "private_channel"}
      <div class="px-2 my-4 flex flex-col">
        <h3 class="mt-2">Admins assigned to private channel</h3>

        <div class="text-black">
          <Svelecte
            name="channel-admins"
            bind:value={selected_admins}
            valueAsObject
            multiple
            required
            renderer="user-blocks"
            placeholder="Search for members"
            class="svelecte-control text-black"
            fetch="/mentions?value_like=[query]"
          />
        </div>

        <h3 class="mt-2">Read-Write Members assigned to private channel</h3>

        <div class="text-black">
          <Svelecte
            name="channel-authors"
            bind:value={selected_authors}
            valueAsObject
            multiple
            required
            renderer="user-blocks"
            placeholder="Search for members"
            class="svelecte-control text-black"
            fetch="/mentions?value_like=[query]"
          />
        </div>

        <h3 class="mt-2">Read-Only Members assigned to private channel</h3>

        <div class="text-black">
          <Svelecte
            name="channel-readers"
            bind:value={selected_readers}
            valueAsObject
            multiple
            required
            renderer="user-blocks"
            placeholder="Search for members"
            class="svelecte-control text-black"
            fetch="/mentions?value_like=[query]"
          />
        </div>
      </div>
    {/if}

    <div class="p-4 text-center">
      <button class="btn big primary" disabled={saving} onclick={saveChannel}>
        {status === "edit" ? "Update" : "Create"}
      </button>
    </div>
  {/if}
</form>
